import { useQuery } from "@apollo/client";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Maximize from "@mui/icons-material/Maximize";
import Minimize from "@mui/icons-material/Minimize";
import {
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { MinimizableDialog } from "components/MinimizableDialog";
import {
  DocumentAiEnrichmentJobQuery,
  ExtractionStatus,
  JobStatus,
} from "generated-graphql/graphql";
import { terminalJobStatuses } from "hooks/useLongPollingForJob";
import { useTenant } from "hooks/useTenant";
import pluralize from "pluralize";
import { useDocumentReview } from "providers/documentReview";
import { useMinimizedDialogs } from "providers/minimizedDialogs";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DOCUMENT_AI_ENRICHMENT_JOB_STATUS } from "./queries";

function ProgressChip({
  progressCount,
  totalCount,
  isFailed,
  isRunning,
  isSucceeded,
}: {
  progressCount: number;
  totalCount: number;
  isFailed: boolean;
  isRunning: boolean;
  isSucceeded: boolean;
}) {
  const completedSuccessfully = isSucceeded && progressCount === totalCount;
  const completedUnsuccessfully = isSucceeded && progressCount < totalCount;
  if (isFailed || completedUnsuccessfully) {
    return (
      <Chip
        icon={<CloseIcon fontSize="small" color="error" />}
        label={`${progressCount} of ${totalCount}`}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color: "error.main",
          backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
        }}
      />
    );
  }

  if (completedSuccessfully) {
    return (
      <Chip
        icon={<CheckIcon fontSize="small" color="success" />}
        label={`${progressCount} of ${totalCount}`}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color: "success.main",
          backgroundColor: (theme) => alpha(theme.palette.success.main, 0.08),
        }}
      />
    );
  }
  return (
    <Chip
      icon={<CheckIcon fontSize="small" color="info" />}
      label={`${progressCount} of ${totalCount}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        color: "primary.main",
        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
      }}
    />
  );
}

export function DocumentAIAnalyzingDialog({
  open,
  onClose,
  jobId,
  preferOriginalTitle = false,
  dialogId,
}: {
  open: boolean;
  onClose:
    | (() => void)
    | ((
        event: React.MouseEvent<HTMLElement>,
        reason:
          | "backdropClick"
          | "escapeKeyDown"
          | "cancelButtonClick"
          | "closeButtonClick"
      ) => void);
  jobId: string;
  /** Unique ID for this dialog instance (for minimization) */
  dialogId?: string;
  preferOriginalTitle?: boolean;
}) {
  const { minimize, expand, close, dialogs } = useMinimizedDialogs();
  const { setDocumentsToReview } = useDocumentReview();
  const { data, error, stopPolling } = useQuery(
    DOCUMENT_AI_ENRICHMENT_JOB_STATUS,
    {
      variables: { id: jobId },
      pollInterval: 1000,
    }
  );

  useEffect(() => {
    if (
      error ||
      (data?.documentAIEnrichmentJob?.status &&
        terminalJobStatuses.includes(data.documentAIEnrichmentJob.status))
    ) {
      stopPolling();
    }
  }, [
    data?.documentAIEnrichmentJob?.status,
    data?.documentAIEnrichmentJob?.documents,
    error,
    stopPolling,
    setDocumentsToReview,
  ]);

  const { tenantId } = useTenant();
  const navigate = useNavigate();

  // Generate dialog ID if not provided
  const currentDialogId = dialogId || `document-ai-analyzing-${jobId}`;

  // Get dialog mode
  const dialogMode =
    dialogs.find((d) => d.id === currentDialogId)?.mode ?? "centered";

  // Get job state from query
  const status: JobStatus | undefined = data?.documentAIEnrichmentJob?.status;
  const isRunning =
    status === JobStatus.Queued ||
    status === JobStatus.Dispatched ||
    status === JobStatus.Running ||
    status === undefined; // initial state
  const isSucceeded = status === JobStatus.Succeeded;
  const isFailed = status === JobStatus.Failed;

  const analysisStats = useMemo(() => {
    const documents = data?.documentAIEnrichmentJob?.documents ?? [];
    const succeededDocuments = documents.filter(
      (document) => document.extractionStatus === ExtractionStatus.Succeeded
    ).length;
    return {
      totalDocuments: documents.length,
      succeededDocuments,
    };
  }, [data?.documentAIEnrichmentJob?.documents]);

  const progressCount = analysisStats.succeededDocuments;
  const totalCount = analysisStats.totalDocuments;

  const handleMinimize = useCallback(() => {
    minimize(currentDialogId);
  }, [currentDialogId, minimize]);

  const handleExpand = useCallback(() => {
    expand(currentDialogId);
  }, [currentDialogId, expand]);

  // Always render MinimizableDialog component, with mode prop
  return (
    <MinimizableDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      mode={dialogMode}
      title={
        <TitleBar
          progressCount={progressCount}
          totalCount={totalCount}
          isFailed={isFailed}
          isRunning={isRunning}
          isSucceeded={isSucceeded}
          numDocuments={totalCount}
        />
      }
      hideCloseButton={true}
      titleBarActionElement={
        <IconButton
          onClick={
            dialogMode === "minimizedInTray" ? handleExpand : handleMinimize
          }
        >
          {dialogMode === "minimizedInTray" ? <Maximize /> : <Minimize />}
        </IconButton>
      }
      noPrimaryAction
      additionalPrimaryActionElements={[
        <Button
          key="review-and-confirm"
          loading={isRunning}
          disabled={!isSucceeded}
          onClick={() => {
            const documentIds =
              data?.documentAIEnrichmentJob.documents
                .filter(
                  (doc) => doc.extractionStatus === ExtractionStatus.Succeeded
                )
                .map((doc) => doc.id) ?? [];

            if (documentIds.length > 0 && tenantId) {
              setDocumentsToReview(documentIds);
              navigate(`/o/${tenantId}/documents`);
            }
            close(currentDialogId);
          }}
          variant="text"
          size="medium"
        >
          {isSucceeded && progressCount === 0 ? "Exit" : "Review and Confirm"}
        </Button>,
      ]}
      cancelButtonProps={{ text: isRunning ? "Close" : "Done" }}
    >
      <Stack spacing={2}>
        <Box>
          <LinearProgress
            value={
              isRunning && totalCount > 0
                ? (progressCount / totalCount) * 100
                : 100
            }
            variant={
              isRunning && progressCount === 0 ? "indeterminate" : "determinate"
            }
          />

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {isRunning &&
              "Hang tight, we're reading your uploads and filling in what we can. This usually takes about 5-10 seconds per file, though larger files may take longer. We'll let you know when everything is ready for you to review."}
            {isSucceeded &&
              `Your documents have been uploaded and we have filled in as much information as we could. When you have time, please review document classifications and details for each upload.`}
            {isFailed && "Analysis failed."}
          </Typography>
        </Box>

        <List dense>
          {data?.documentAIEnrichmentJob?.documents.map((documentRecord) => {
            return (
              <DocumentAIAnalyzingRow
                key={documentRecord.id}
                documentRecord={documentRecord}
                preferOriginalTitle={preferOriginalTitle}
              />
            );
          })}
        </List>
      </Stack>
    </MinimizableDialog>
  );
}

function TitleBar({
  progressCount,
  totalCount,
  isFailed,
  isRunning,
  isSucceeded,
  numDocuments,
}: {
  progressCount: number;
  totalCount: number;
  isFailed: boolean;
  isRunning: boolean;
  isSucceeded: boolean;
  numDocuments: number;
}) {
  const title = useMemo(() => {
    const pluralizedCount = pluralize("Document", numDocuments);
    if (isRunning) {
      return `📑 Analyzing ${numDocuments} ${pluralizedCount}...`;
    }
    if (isSucceeded) {
      return `🔔 Finished Analyzing  ${pluralizedCount}`;
    }
    if (isFailed) {
      return `❌ Analysis failed.`;
    }
    return `📑 Analysis complete.`;
  }, [isRunning, numDocuments, isSucceeded, isFailed]);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: (theme) => theme.spacing(2),
      }}
    >
      <Typography variant="h6">{title}</Typography>
      <ProgressChip
        progressCount={progressCount}
        totalCount={totalCount}
        isFailed={isFailed}
        isRunning={isRunning}
        isSucceeded={isSucceeded}
      />
    </Box>
  );
}

function DocumentAIAnalyzingRow({
  documentRecord,
  preferOriginalTitle = false,
}: {
  documentRecord:
    | DocumentAiEnrichmentJobQuery["documentAIEnrichmentJob"]["documents"][number];
  preferOriginalTitle?: boolean;
}) {
  return (
    <ListItem
      key={documentRecord.id}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {preferOriginalTitle
            ? documentRecord.originalTitle ?? documentRecord.title
            : documentRecord.title}
        </Typography>
      </Box>
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 0.5,
        }}
      >
        <ExtractionStatusDisplay
          extractionStatus={
            documentRecord?.extractionStatus as
              | ExtractionStatus
              | null
              | undefined
          }
          extractionError={documentRecord?.extractionError ?? null}
        />
      </Box>
    </ListItem>
  );
}

const ExtractionStatusDisplay = ({
  extractionStatus,
  extractionError,
}: {
  extractionStatus: ExtractionStatus | null | undefined;
  extractionError: string | null;
}) => {
  switch (extractionStatus) {
    case ExtractionStatus.Pending:
    case ExtractionStatus.NotAttempted:
    case undefined:
    default:
      return (
        <Typography variant="body2" color="primary.main">
          In progress...
        </Typography>
      );
    case ExtractionStatus.Succeeded:
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Typography variant="body2" color="success.main">
            Done
          </Typography>
          <CheckIcon fontSize="small" color="success" />
        </Box>
      );
    case ExtractionStatus.Failed:
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Tooltip title={extractionError ?? "Extraction failed"}>
            <ErrorOutlineIcon color="error" fontSize="small" />
          </Tooltip>
          <Typography variant="body2" color="error.main">
            Failed
          </Typography>
        </Box>
      );
  }
};
